/** Client Campaign helpers. */

export const CAMPAIGN_MODE_OPTIONS: Array<{ id: string; label: string }> = [
  { id: "normal", label: "Normal" },
  { id: "wasd", label: "WASD" },
  { id: "superdot", label: "Superdot" },
  { id: "roulette", label: "Roulette" },
  { id: "highway", label: "Highway" },
  { id: "darkness", label: "Darkness" },
  { id: "mirror", label: "Mirror" },
  { id: "snake", label: "Snake" },
  { id: "combo", label: "Combo" },
  { id: "colorrush", label: "Color Rush" },
  { id: "heat", label: "Heat" },
  { id: "hunter", label: "Hunter" },
  { id: "pop", label: "POP" },
  { id: "counter", label: "Counter" },
  { id: "aim", label: "AIM" },
  { id: "hook", label: "Hook" },
  { id: "tilt", label: "Tilt" },
  { id: "orbit", label: "Orbit" },
  { id: "delay", label: "Delay" },
  { id: "progression", label: "Progression" },
  { id: "speedrun", label: "SpeedRun" },
  { id: "penalty", label: "Golf" },
  { id: "sandbox", label: "Sandbox" },
];

export type CampaignNodeState = "locked" | "available" | "completed";
export type CampaignRewardType = "dots" | "item" | "both";

export const CAMPAIGN_NODE_DIAMETER_DEFAULT = 44;
export const CAMPAIGN_NODE_DIAMETER_FINAL = 68;
export const CAMPAIGN_NODE_DIAMETER_MIN = 28;
export const CAMPAIGN_NODE_DIAMETER_MAX = 140;

export type CampaignStateColors = {
  locked?: string;
  available?: string;
  completed?: string;
};

export function campaignNodeDiameterPx(node: {
  nodeDiameter?: number | null;
  isFinal?: boolean;
}): number {
  const raw = Math.round(Number(node.nodeDiameter) || 0);
  if (raw >= CAMPAIGN_NODE_DIAMETER_MIN && raw <= CAMPAIGN_NODE_DIAMETER_MAX) return raw;
  return node.isFinal ? CAMPAIGN_NODE_DIAMETER_FINAL : CAMPAIGN_NODE_DIAMETER_DEFAULT;
}

export function campaignModeNeedsLevel(modeId?: string | null): boolean {
  return modeId === "speedrun" || modeId === "penalty";
}

export function campaignModeIsRace(modeId?: string | null): boolean {
  return modeId === "speedrun" || modeId === "penalty";
}

export type CampaignNodeView = {
  id: string;
  modeId: string;
  modeLabel: string;
  metric: "score" | "time";
  goalValue: number;
  levelIndex?: number | null;
  layoutRef?: string | null;
  posX: number;
  posY: number;
  parentIds: string[];
  rewardType: CampaignRewardType;
  rewardDots: number;
  rewardCosmeticId: string | null;
  /** Circle diameter px; null/undefined = default. */
  nodeDiameter?: number | null;
  /** Optional fill colors per unlock state. */
  stateColors?: {
    locked?: string;
    available?: string;
    completed?: string;
  } | null;
  label: string;
  active: boolean;
  sortOrder: number;
  state?: CampaignNodeState;
  isFinal?: boolean;
  rewardClaimed?: boolean;
};

export type CampaignAttempt = {
  nodeId: string;
  modeId: string;
  metric: "score" | "time";
  goalValue: number;
  token: string;
  modeLabel: string;
  levelIndex?: number | null;
  layoutRef?: string | null;
};

export type CampaignTreeResponse = {
  nodes: CampaignNodeView[];
  completedNodeIds: string[];
  claimedNodeIds: string[];
  active: CampaignAttempt | null;
  message?: string;
};

export function formatCampaignGoalLine(
  modeLabel: string,
  metric: "score" | "time",
  goalValue: number,
  modeId?: string,
  levelIndex?: number | null,
): string {
  if (campaignModeIsRace(modeId)) {
    const lv = Math.max(1, Math.floor(Number(levelIndex) || 0));
    const limit = Math.max(1, Math.floor(goalValue));
    return lv >= 1 ? `${modeLabel} — finish in ${limit}s` : `${modeLabel} — ${limit}s`;
  }
  if (metric === "time") return `${modeLabel} — ${Math.max(1, Math.floor(goalValue))}s`;
  if (modeId === "progression") return `${modeLabel} — Level ${Math.max(1, Math.floor(goalValue))}`;
  return `${modeLabel} — ${Math.max(1, Math.floor(goalValue))} score`;
}

export function formatCampaignGoalReveal(
  modeLabel: string,
  metric: "score" | "time",
  goalValue: number,
  modeId?: string,
  levelIndex?: number | null,
): [string, string] {
  if (campaignModeIsRace(modeId)) {
    const lv = Math.max(1, Math.floor(Number(levelIndex) || 0));
    const limit = Math.max(1, Math.floor(goalValue));
    const levelBit = lv >= 1 ? ` level ${lv}` : "";
    return [modeLabel, `Finish${levelBit} within ${limit}s`];
  }
  if (metric === "time") return [modeLabel, `Goal: ${Math.max(1, Math.floor(goalValue))}s`];
  if (modeId === "progression") {
    return [modeLabel, `Goal: Level ${Math.max(1, Math.floor(goalValue))}`];
  }
  return [modeLabel, `Goal: ${Math.max(1, Math.floor(goalValue))} score`];
}
